import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Bot, MessageCircle, Languages } from "lucide-react";

const SCRIPT = [
  {
    from: "user",
    text: "Hi! I run a saree shop in Surat. How do I sell online?",
    delay: 0,
  },
  {
    from: "bot",
    text: "Namaste! 🙏 I can help. Let's start with a Hindi + English store, UPI checkout and WhatsApp orders.",
    delay: 1100,
  },
  { from: "user", text: "Kitne din lagenge?", delay: 2400 },
  {
    from: "bot",
    text: "Sirf 48 ghante! Aapki saree photos aur GST mil jayein, hum live kar denge.",
    delay: 3400,
  },
  {
    from: "bot",
    text: "Shall I draft your homepage tagline now?",
    delay: 4500,
  },
];

const Bubble = ({ msg, i }) => {
  const isBot = msg.from === "bot";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className={`flex ${isBot ? "justify-start" : "justify-end"}`}
      data-testid={`chat-bubble-${i}`}
    >
      <div
        className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-snug ${
          isBot
            ? "bg-foreground/[0.06] dark:bg-white/[0.06] border border-border rounded-tl-sm"
            : "bg-gradient-to-br from-[#FF7A00] to-[#FF512F] text-white rounded-tr-sm shadow-[0_6px_20px_rgba(255,122,0,0.35)]"
        }`}
      >
        {msg.text}
      </div>
    </motion.div>
  );
};

export const ChatbotPreview = () => {
  const [visible, setVisible] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    const timeouts = [];
    const runScript = () => {
      setVisible(0);
      setTyping(false);
      SCRIPT.forEach((m, i) => {
        const t1 = setTimeout(() => {
          if (m.from === "bot") setTyping(true);
        }, Math.max(0, m.delay - 500));
        const t2 = setTimeout(() => {
          setTyping(false);
          setVisible((v) => Math.max(v, i + 1));
        }, m.delay);
        timeouts.push(t1, t2);
      });
    };
    runScript();
    const loop = setInterval(runScript, 8500);
    return () => {
      clearInterval(loop);
      timeouts.forEach(clearTimeout);
    };
  }, []);

  return (
    <section
      id="chatbot"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="chatbot-section"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5 space-y-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-[#FF9933]" />
            AI Chat Board
          </div>
          <h2
            className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]"
            data-testid="chatbot-heading"
          >
            A 24×7 sales agent that
            <br />
            <span className="text-gradient-saffron">speaks Bharat.</span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-md">
            Trained on your products, FAQs and tone. Captures leads, books
            appointments and closes WhatsApp orders — even at 2 AM.
          </p>

          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div className="dark:glass-dark glass-light rounded-2xl p-4">
              <Languages className="h-5 w-5 text-[#FF9933] mb-2" />
              <div className="text-2xl font-heading font-extrabold">10+</div>
              <div className="text-xs text-muted-foreground">
                Indic languages
              </div>
            </div>
            <div className="dark:glass-dark glass-light rounded-2xl p-4">
              <MessageCircle className="h-5 w-5 text-[#00C853] mb-2" />
              <div className="text-2xl font-heading font-extrabold">3.4×</div>
              <div className="text-xs text-muted-foreground">
                Lead conversion lift
              </div>
            </div>
          </div>
        </motion.div>

        {/* Phone mock */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 flex justify-center"
        >
          <div className="relative w-full max-w-md">
            {/* Glow */}
            <div className="absolute -inset-8 -z-10 rounded-[2.5rem] bg-gradient-to-br from-[#FF7A00]/30 via-transparent to-[#00C853]/30 blur-3xl" />

            <div
              className="rounded-[2rem] dark:glass-dark glass-light border border-border overflow-hidden shadow-2xl"
              data-testid="chatbot-preview"
            >
              {/* Header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
                <div className="relative h-10 w-10 rounded-xl gradient-saffron grid place-items-center">
                  <Bot className="h-5 w-5 text-white" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#00C853] border-2 border-background" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">DigIndia Assist</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#00C853]" />
                    Online · Replies in seconds
                  </div>
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                  AI
                </div>
              </div>

              {/* Body */}
              <div className="p-5 space-y-3 min-h-[340px] max-h-[380px] overflow-hidden bg-gradient-to-b from-transparent to-foreground/[0.02]">
                {SCRIPT.slice(0, visible).map((m, i) => (
                  <Bubble key={i} msg={m} i={i} />
                ))}
                <AnimatePresence>
                  {typing && visible < SCRIPT.length && (
                    <motion.div
                      key="typing"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex justify-start"
                      data-testid="chat-typing"
                    >
                      <div className="px-3 py-2.5 rounded-2xl rounded-tl-sm bg-foreground/[0.06] dark:bg-white/[0.06] border border-border flex gap-1">
                        {[0, 1, 2].map((d) => (
                          <span
                            key={d}
                            className="h-1.5 w-1.5 rounded-full bg-foreground/60 animate-bounce"
                            style={{ animationDelay: `${d * 120}ms` }}
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Input */}
              <div className="border-t border-border p-3 flex items-center gap-2">
                <div className="flex-1 px-4 py-2.5 rounded-full bg-foreground/[0.04] text-sm text-muted-foreground">
                  Type a message…
                </div>
                <button
                  className="h-10 w-10 grid place-items-center rounded-full gradient-saffron text-white shadow-[0_6px_20px_rgba(255,122,0,0.4)]"
                  aria-label="Send"
                  data-testid="chatbot-send-button"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ChatbotPreview;
